/**
 * Drawing a shape's outline.
 *
 * Separate from the ink painter beside it for one reason: ink is drawn as a run of
 * curves through the points, which is exactly right for handwriting and exactly wrong
 * for a rectangle — it would round off every corner. A shape is drawn as the straight
 * pieces it actually is, so its corners stay corners at any zoom.
 *
 * The width follows SRS C3 like everything else on the board: canvas-space width in
 * the record, screen-space width on the glass.
 */

import { canvasToScreen, type Point, type Viewport } from '@opal/shared';

import { flatWidth } from './ink.js';

export interface OutlineStyle {
  readonly color: string;
  /** Canvas-space, exactly like `Stroke.canvasWidth`. */
  readonly canvasWidth: number;
  readonly opacity?: number;
}

/**
 * Draws an outline in one path.
 *
 * One path rather than one per piece, so the corners join cleanly and a see-through
 * line never doubles up on itself where two pieces meet.
 */
export function drawOutline(
  ctx: CanvasRenderingContext2D,
  points: readonly Point[],
  style: OutlineStyle,
  viewport: Viewport,
): void {
  const first = points[0];
  if (!first) return;

  ctx.save();
  ctx.strokeStyle = style.color;
  ctx.globalAlpha = style.opacity ?? 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = flatWidth(viewport, style.canvasWidth);

  ctx.beginPath();
  const start = canvasToScreen(viewport, first);
  ctx.moveTo(start.x, start.y);
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (!point) continue;
    const at = canvasToScreen(viewport, point);
    ctx.lineTo(at.x, at.y);
  }

  // A shape drawn with a single point — a tap — would leave nothing at all without
  // this, and the teacher would be left wondering whether the tool was working.
  if (points.length === 1) {
    ctx.lineTo(start.x + 0.01, start.y);
  }
  ctx.stroke();
  ctx.restore();
}
